import { Box, Text, useColorModeValue } from '@ecoinvent/ui';
import { Handle, NodeProps, Position } from '@xyflow/react';

import { SUPPLY_NODE_WIDTH } from './constants';
import { stopEvents } from './utils';

export type OverflowNodeData = {
  remaining: number;
  // Resolved by the layout so the overflow card is always as tall as the real cards it sits beside.
  nodeHeight: number;
  onShowMore: () => void;
};

const invisibleHandle: React.CSSProperties = {
  background: 'transparent',
  border: 'none',
  width: 1,
  height: 1,
  top: '50%',
};

// The digits the card renders and its accessible name read the same count, so the two cannot disagree.
const overflowLabel = (remaining: number | undefined): string =>
  remaining === undefined ? 'Show more activities' : `Show ${remaining} more ${remaining === 1 ? 'activity' : 'activities'}`;

const OverflowNode = ({ data }: NodeProps) => {
  const { remaining, nodeHeight, onShowMore } = data as OverflowNodeData;
  const bg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('green.300', 'green.600');
  const greenColor = useColorModeValue('green.500', 'green.400');
  const subtextColor = useColorModeValue('gray.400', 'gray.500');

  return (
    <Box
      as="button"
      type="button"
      aria-label={overflowLabel(remaining)}
      bg={bg}
      border="1.5px dashed"
      borderColor={borderColor}
      borderRadius="md"
      p={3}
      w={`${SUPPLY_NODE_WIDTH}px`}
      h={`${nodeHeight}px`}
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      cursor="pointer"
      className="nopan nodrag"
      onPointerDown={stopEvents}
      onMouseDown={stopEvents}
      onClick={(e: React.MouseEvent) => {
        stopEvents(e);
        onShowMore();
      }}
    >
      <Handle type="target" position={Position.Left} style={invisibleHandle} />
      <Text color={greenColor} fontWeight="bold" fontSize="xl">
        +{remaining} more
      </Text>
      <Text color={subtextColor} fontSize="sm">
        Show hidden activities
      </Text>
      <Handle type="source" position={Position.Right} style={invisibleHandle} />
    </Box>
  );
};

export default OverflowNode;
